import { useRef, useCallback } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './Hero.css';

const charVariants = {
  hidden: { opacity: 0, y: 50, filter: 'blur(14px)' },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
      delay: 0.4 + i * 0.045,
    },
  }),
};

const fadeUpBlur = {
  hidden: { opacity: 0, y: 30, filter: 'blur(10px)' },
  visible: (delay) => ({
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 1.0,
      ease: [0.16, 1, 0.3, 1],
      delay,
    },
  }),
};

export default function Hero() {
  const heroRef = useRef(null);
  const { scrollY } = useScroll();

  const contentOpacity = useTransform(scrollY, [0, 500], [1, 0]);
  const contentY = useTransform(scrollY, [0, 500], [0, 80]);
  const scrollIndicatorOpacity = useTransform(scrollY, [0, 200], [1, 0]);

  const name = 'Leo Amiri';

  const handleMouseMove = useCallback((e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    heroRef.current.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    heroRef.current.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  }, []);

  return (
    <section className="hero" id="hero" ref={heroRef} onMouseMove={handleMouseMove}>
      <div className="hero-spotlight" />

      <motion.div className="hero-content" style={{ opacity: contentOpacity, y: contentY }}>
        <h1 className="hero-name" aria-label={name}>
          {name.split('').map((char, i) => (
            <motion.span
              key={i}
              className="hero-char"
              custom={i}
              initial="hidden"
              animate="visible"
              variants={charVariants}
              aria-hidden="true"
            >
              {char === ' ' ? '\u00A0' : char}
            </motion.span>
          ))}
        </h1>

        <motion.p
          className="hero-title"
          custom={0.85}
          initial="hidden"
          animate="visible"
          variants={fadeUpBlur}
        >
          Full-Stack Engineer & AI Product Builder
        </motion.p>

        <motion.p
          className="hero-description"
          custom={1.05}
          initial="hidden"
          animate="visible"
          variants={fadeUpBlur}
        >
          I design, build, and ship digital products from the ground up, from architecture to launch.
        </motion.p>
      </motion.div>

      <motion.div className="hero-scroll" style={{ opacity: scrollIndicatorOpacity }}>
        <motion.span
          className="hero-scroll-text"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
        >
          Scroll
        </motion.span>
        <motion.div
          className="hero-scroll-line"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ delay: 1.8, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        />
      </motion.div>
    </section>
  );
}
